import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Pressable } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import type { IconName } from '@kiips/ui';
import { AccentButton, BaseSheet, Button, useAppToast, Icon } from '@kiips/ui';
import { colors } from '../../theme/colors';
import { useThemeColors } from '../../hooks/useThemeColors';
import { usePlanUpgrade } from '../../hooks/usePlanUpgrade';
import {
  annualDiscountPercent,
  type BillingPeriod,
  DEFAULT_TRIAL_DAYS,
  formatAnnualAsMonthly,
  formatPrice,
  getPlan,
  type PlanId,
} from '../../lib/pricing-plans';
import { track } from '../../lib/analytics';

interface UpgradeSheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional eyebrow above the title — e.g. the gated feature that
   *  triggered the sheet ("Opening hours", "More trips"). Drives copy
   *  personalization via TRIGGER_COPY. */
  trigger?: string | undefined;
  /** Pre-select a plan in the Just-me / Family toggle. Useful when the
   *  caller is the /family upsell card or a Family-only feature — the
   *  toggle is still interactive so the user can swap back to Pro. */
  defaultPlan?: 'pro' | 'pro-family' | undefined;
}

/** Per-trigger personalization. The default (`null` key) is used when
 *  the trigger is unknown — shows the generic "Unlock Pro" hero. New
 *  triggers should add an entry here and the hero will adapt
 *  automatically. Lead-feature labels MUST also appear in
 *  pricing-plans.PRO_FEATURES so we don't drift between surfaces. */
type TriggerCopy = {
  eyebrow: string;
  title: string;
  leadFeature: string;
};

const TRIGGER_COPY: Record<string, TriggerCopy> = {
  'More trips': {
    eyebrow: 'UNLIMITED TRIPS · PRO',
    title: 'Plan as many trips as you want',
    leadFeature: 'Unlimited private trips',
  },
  Sharing: {
    eyebrow: 'TRIP SHARING · PRO',
    title: 'Plan together, in real time',
    leadFeature: 'Trip sharing — invite collaborators',
  },
  Weather: {
    eyebrow: 'WEATHER · PRO',
    title: 'Plan around the forecast',
    leadFeature: 'Per-day weather forecasts',
  },
  'Opening hours': {
    eyebrow: 'OPENING HOURS · PRO',
    title: "See what's open when you arrive",
    leadFeature: 'Place opening hours on cards',
  },
  'Cost tracking': {
    eyebrow: 'COST TRACKING · PRO',
    title: 'See what your trip really costs',
    leadFeature: 'Multi-currency cost tracking',
  },
  'Live traffic': {
    eyebrow: 'LIVE TRAFFIC · PRO',
    title: 'Routes that match the real world',
    leadFeature: 'Live-traffic routing + transit schedules',
  },
  MCP: {
    eyebrow: 'CLAUDE + CURSOR · PRO',
    title: 'Plan with AI assistants',
    leadFeature: 'MCP server for Claude / Cursor',
  },
};

const DEFAULT_COPY: TriggerCopy = {
  eyebrow: 'KIIPS PRO',
  title: 'Unlock Kiips Pro',
  leadFeature: 'Unlimited private trips',
};

/**
 * Modal explaining what Pro unlocks, with monthly/annual + just-me/family
 * toggles and a single trial-CTA. ADR-0062 rolls in:
 *
 * - Per-trigger personalization (eyebrow, title, and lead feature pulled
 *   from TRIGGER_COPY).
 * - Two toggles: plan ('pro' / 'pro-family') and billing period
 *   ('monthly' / 'annual', monthly default; annual carries the savings badge).
 * - Live price display and trial microcopy under the CTA.
 *
 * The upgrade button calls `billing.createUpgradeCheckout` — the
 * server picks the Stripe price ID for the requested (tier,
 * billingPeriod) combo, so the client never sees price IDs.
 */
export function UpgradeSheet({ open, onClose, trigger, defaultPlan = 'pro' }: UpgradeSheetProps) {
  const theme = useThemeColors();
  const toast = useAppToast();
  const [plan, setPlan] = useState<Extract<PlanId, 'pro' | 'pro-family'>>(defaultPlan);
  // Monthly leads; annual is the discounted upsell (ADR-0062, monthly-first
  // amendment 2026-06).
  const [billing, setBilling] = useState<BillingPeriod>('monthly');
  // Free user → Checkout (new sub); existing subscriber → in-place
  // changePlan (no redirect). On a real change confirm with a toast (the
  // sheet just closing leaves a same-tier period switch with no visible
  // feedback), then close. Shared with /pricing via usePlanUpgrade (ADR-0088).
  const { hasPaidSub, mode, startUpgrade, busy, error, clearError } = usePlanUpgrade({
    trigger,
    onCheckoutRedirect: onClose,
    onChanged: (tier) => {
      toast.success(tier === 'pro-family' ? "You're on Family Pro" : "You're on Pro", {
        description: 'Your plan change is live.',
      });
      onClose();
    },
    onNoop: onClose,
  });
  // True once the user has clicked through to Stripe — distinguishes a
  // genuine abandonment (closed the sheet) from the onSuccess close that
  // also calls onClose() after launching checkout. The sheet is a single
  // always-mounted instance (UpgradeSheetProvider), so reset it — and any
  // stale error from a prior attempt — on each open.
  const startedCheckoutRef = useRef(false);
  useEffect(() => {
    if (open) {
      startedCheckoutRef.current = false;
      clearError();
    }
  }, [open, clearError]);

  const copy = trigger ? TRIGGER_COPY[trigger] ?? DEFAULT_COPY : DEFAULT_COPY;
  const planDescriptor = getPlan(plan);
  const price = planDescriptor.price;

  // Reorder so the trigger's lead feature is row 1. The slice keeps
  // every other entry in its original order — the list stays
  // recognizable across triggers.
  const featureRows = useMemo(() => {
    const features = planDescriptor.features.filter((f) => f.included);
    const leadIdx = features.findIndex((f) => f.label === copy.leadFeature);
    if (leadIdx <= 0) return features;
    return [features[leadIdx], ...features.slice(0, leadIdx), ...features.slice(leadIdx + 1)];
  }, [planDescriptor, copy]);

  const handleUpgrade = () => {
    startedCheckoutRef.current = true;
    if (hasPaidSub) {
      track('billing.plan_change_started', {
        tier: plan,
        billingPeriod: billing,
        trigger: trigger ?? null,
      });
    } else {
      track('billing.checkout_started', {
        tier: plan,
        billingPeriod: billing,
        trigger: trigger ?? null,
        // Display price for funnel valuation. Server-side
        // billing.subscription_activated carries the authoritative
        // Stripe amount; this is the intent-time figure. Currency is
        // lowercase to match Stripe's canonical code on the server events
        // (PostHog treats 'usd'/'USD' as distinct dimension values).
        amount: price ? price[billing] : null,
        currency: 'usd',
      });
    }
    startUpgrade(plan, billing);
  };

  const handleClose = () => {
    // Only an abandonment if they never launched checkout — the
    // onSuccess path also routes through onClose() after opening Stripe.
    if (!startedCheckoutRef.current) {
      // Attribute the abandonment to the funnel the user was actually in
      // — an existing subscriber sees the in-app change flow, not Checkout.
      track(hasPaidSub ? 'billing.plan_change_abandoned' : 'billing.checkout_abandoned', {
        tier: plan,
        billingPeriod: billing,
        trigger: trigger ?? null,
      });
    }
    clearError();
    onClose();
  };

  const priceText = price ? formatPrice(price, billing) : '';
  const subPriceText = price && billing === 'annual' ? formatAnnualAsMonthly(price) : null;
  const trialMicro = price
    ? `${DEFAULT_TRIAL_DAYS} days free, then ${priceText}${
        plan === 'pro-family' ? ' for up to 5 people' : ''
      } · Cancel anytime.`
    : '';
  // Existing subscribers don't get a trial on a plan change — they're
  // moving an active sub, so the CTA and microcopy reflect a prorated
  // switch rather than a free-trial start (ADR-0088).
  const planLabel = plan === 'pro-family' ? 'Family Pro' : 'Pro';
  const ctaLabel = hasPaidSub ? `Switch to ${planLabel}` : 'Start 7-day free trial';
  const ctaPendingLabel = hasPaidSub ? 'Switching…' : 'Opening checkout…';
  const microcopy = hasPaidSub
    ? price
      ? `Switch now — ${priceText}${
          plan === 'pro-family' ? ' for up to 5 people' : ''
        }. Prorated on your next invoice.`
      : ''
    : trialMicro;

  // Wire the sheet's title as its accessible name so screen readers (and
  // the Lighthouse "dialog has no accessible name" audit) can announce
  // what the dialog is for.
  const titleId = useId();

  return (
    <BaseSheet
      open={open}
      onOpenChange={(o) => !o && handleClose()}
      snapPoints={[72]}
      ariaLabelledBy={titleId}
    >
      <YStack gap="$3">
        <Text fontSize={11} fontWeight="700" letterSpacing={0.6} color={colors.accentCerulean}>
          {copy.eyebrow}
        </Text>
        <Text id={titleId} fontSize={22} fontWeight="700" color={theme.textOnSurface}>
          {copy.title}
        </Text>

        <PlanToggle value={plan} onChange={setPlan} />
        <BillingToggle
          value={billing}
          onChange={setBilling}
          discountPercent={annualDiscountPercent(planDescriptor.price)}
        />

        {price ? (
          <YStack gap={2}>
            <Text fontSize={28} fontWeight="800" color={theme.textOnSurface}>
              {priceText}
              {plan === 'pro-family' ? (
                <Text fontSize={14} fontWeight="600" color={theme.textSubdued}>
                  {'  · up to 5 people'}
                </Text>
              ) : null}
            </Text>
            {subPriceText ? (
              <Text fontSize={12} color={theme.textSubdued}>
                {subPriceText}
              </Text>
            ) : null}
          </YStack>
        ) : null}

        <YStack gap="$2" marginTop="$2">
          {featureRows.map((f) => (
            <FeatureRow key={f!.label} icon={iconForLabel(f!.label)} label={f!.label} />
          ))}
        </YStack>

        {error ? (
          <Text fontSize={13} color={colors.accentCrimson} marginTop="$2">
            {error}
          </Text>
        ) : null}

        <YStack gap="$2" marginTop="$3">
          {mode === 'off' ? null : (
            <AccentButton
              onPress={handleUpgrade}
              disabled={busy}
              accessibilityLabel={ctaLabel}
            >
              {busy ? ctaPendingLabel : ctaLabel}
            </AccentButton>
          )}
          {mode !== 'off' && microcopy ? (
            <Text fontSize={12} color={theme.textSubdued} textAlign="center">
              {microcopy}
            </Text>
          ) : null}
          <Button
            onPress={handleClose}
            backgroundColor="transparent"
            color={theme.textSubdued}
            accessibilityLabel="Maybe later"
          >
            Maybe later
          </Button>
        </YStack>
      </YStack>
    </BaseSheet>
  );
}

function PlanToggle({
  value,
  onChange,
}: {
  value: 'pro' | 'pro-family';
  onChange: (v: 'pro' | 'pro-family') => void;
}) {
  return (
    <SegmentRow>
      <Segment active={value === 'pro'} onPress={() => onChange('pro')} label="Just me" />
      <Segment
        active={value === 'pro-family'}
        onPress={() => onChange('pro-family')}
        label="Family (5 seats)"
      />
    </SegmentRow>
  );
}

function BillingToggle({
  value,
  onChange,
  discountPercent,
}: {
  value: BillingPeriod;
  onChange: (v: BillingPeriod) => void;
  /** Computed from the selected plan's prices so the badge can't drift. */
  discountPercent: number;
}) {
  return (
    <SegmentRow>
      <Segment active={value === 'monthly'} onPress={() => onChange('monthly')} label="Monthly" />
      <Segment
        active={value === 'annual'}
        onPress={() => onChange('annual')}
        label="Annual"
        badge={`Save ${discountPercent}%`}
      />
    </SegmentRow>
  );
}

function SegmentRow({ children }: { children: React.ReactNode }) {
  const theme = useThemeColors();
  return (
    <XStack
      backgroundColor={theme.surfaceCanvas}
      borderRadius={999}
      padding={3}
      gap={3}
      alignSelf="stretch"
    >
      {children}
    </XStack>
  );
}

function Segment({
  active,
  onPress,
  label,
  badge,
}: {
  active: boolean;
  onPress: () => void;
  label: string;
  badge?: string | undefined;
}) {
  const theme = useThemeColors();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={`${label}${badge ? ` (${badge})` : ''}`}
      style={{ flex: 1 }}
    >
      <XStack
        flex={1}
        backgroundColor={active ? theme.textOnSurface : 'transparent'}
        paddingVertical={9}
        borderRadius={999}
        alignItems="center"
        justifyContent="center"
        gap={6}
      >
        <Text fontSize={13} fontWeight="700" color={active ? theme.textInverse : theme.textOnSurface}>
          {label}
        </Text>
        {badge ? (
          <Text
            fontSize={10}
            fontWeight="800"
            color={active ? theme.textInverse : colors.accentCerulean}
          >
            {badge}
          </Text>
        ) : null}
      </XStack>
    </Pressable>
  );
}

function iconForLabel(label: string): IconName {
  if (label.includes('trip')) return 'card-travel';
  if (label.includes('sharing') || label.includes('seat')) return 'group';
  if (label.toLowerCase().includes('weather')) return 'wb-cloudy';
  if (label.toLowerCase().includes('opening hours')) return 'schedule';
  if (label.toLowerCase().includes('cost')) return 'payments';
  if (label.toLowerCase().includes('traffic') || label.toLowerCase().includes('rout')) return 'directions';
  if (label.toLowerCase().includes('suggest')) return 'auto-awesome';
  if (label.toLowerCase().includes('flight') || label.toLowerCase().includes('hotel')) return 'flight';
  if (label.toLowerCase().includes('calendar')) return 'event';
  if (label.toLowerCase().includes('offline')) return 'cloud-off';
  if (label.toLowerCase().includes('mcp') || label.toLowerCase().includes('claude')) return 'smart-toy';
  if (label.toLowerCase().includes('support')) return 'support-agent';
  return 'check-circle';
}

function FeatureRow({
  icon,
  label,
}: {
  icon: IconName;
  label: string;
}) {
  const theme = useThemeColors();
  return (
    <XStack gap="$3" alignItems="center">
      <YStack
        width={32}
        height={32}
        borderRadius={10}
        alignItems="center"
        justifyContent="center"
        backgroundColor={colors.accentCerulean + '1A'}
      >
        <Icon name={icon} size={18} color={colors.accentCerulean} />
      </YStack>
      <Text fontSize={14} color={theme.textOnSurface} flex={1}>
        {label}
      </Text>
    </XStack>
  );
}
